/* ============================================================
   ÉPRIS Loyalty UI Hydration v2
   ============================================================
   Hydrated alle Liquid-rendered Loyalty-Components mit live-Daten
   aus window.eprisLoyalty.state. Subscribed auf state-changes.

   Components handled:
   - Header-Badge ([data-loyalty-badge])
   - Mini-Panel ([data-loyalty-panel]) — mit Tabs + Rewards + History + Referral
   - PDP-Section ([data-loyalty-pdp])
   - Cart-Drawer-Card ([data-loyalty-cart])
   - Info-Modal ([data-loyalty-open-info] → #epris-loyalty-info-modal)
   ============================================================ */

(function() {
  'use strict';

  /* Translations — gesetzt vom Liquid-Template via window.EPRIS_TRANS */
  function t(key, fallback) {
    const trans = window.EPRIS_TRANS || {};
    return trans[key] || fallback || '';
  }

  function ready() {
    if (!window.eprisLoyalty) {
      if ((ready._retries = (ready._retries || 0) + 1) > 50) return;
      return setTimeout(ready, 200);
    }
    init();
  }

  function init() {
    window.eprisLoyalty.onUpdate(renderAll);
    renderAll(window.eprisLoyalty.state);

    document.addEventListener('click', onClick);
    document.addEventListener('epris:loyalty:open-panel', openPanel);

    /* Re-render bei DOM-mutationen (cart-update etc.) — debounced */
    let mTimer;
    new MutationObserver(() => {
      clearTimeout(mTimer);
      mTimer = setTimeout(() => renderAll(window.eprisLoyalty.state), 200);
    }).observe(document.body, { childList: true, subtree: true });

    /* Tab-switching delegated */
    document.addEventListener('click', onTabClick);

    /* Esc closes both panel und info-modal */
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const panel = document.getElementById('epris-loyalty-panel');
      const info = document.getElementById('epris-loyalty-info-modal');
      if (info && info.classList.contains('is-visible')) closeInfo();
      else if (panel && panel.classList.contains('is-visible')) closePanel();
    });
  }

  function renderAll(state) {
    applyTierAttr(state);
    renderBadge(state);
    renderPanel(state);
    renderPdp(state);
    renderCart(state);
  }

  /* ============== Tier-Attr Cascade ============== */
  function applyTierAttr(state) {
    const tier = (state.tierName || 'silver').toLowerCase().replace('é', 'e');
    document.querySelectorAll('[data-loyalty-tier-attr]').forEach(el => {
      el.dataset.tier = tier;
    });
  }

  /* ============== HEADER BADGE ============== */
  function renderBadge(state) {
    document.querySelectorAll('[data-loyalty-badge]').forEach(badge => {
      const tierEl = badge.querySelector('[data-loyalty-tier-name]');
      const ptsEl = badge.querySelector('[data-loyalty-pts-num]');
      if (tierEl) tierEl.textContent = (state.tierName || 'Silver').toUpperCase();
      if (ptsEl) ptsEl.textContent = (state.points || 0).toLocaleString('de-DE');
    });
  }

  /* ============== MINI PANEL ============== */
  function renderPanel(state) {
    const panel = document.getElementById('epris-loyalty-panel');
    if (!panel) return;

    /* HERO */
    setText(panel, '[data-loyalty-tier-name]', (state.tierName || 'Silver').toUpperCase());
    /* Pts-num: count-up animation auf hero (alle anderen normal) */
    panel.querySelectorAll('[data-loyalty-pts-num]').forEach(el => {
      const target = state.points || 0;
      if (el.hasAttribute('data-loyalty-countup')) animateCountUp(el, target);
      else el.textContent = target.toLocaleString('de-DE');
    });
    const ptsValueEl = panel.querySelector('[data-loyalty-pts-value]');
    if (ptsValueEl) {
      ptsValueEl.textContent = '= ' + formatEuro(state.pointsValueEuros) + ' '
        + t('loyalty.checkout_redeem', 'beim Checkout einlösbar');
    }

    /* OVERVIEW: Tier-Progress */
    const tierProgressLabel = panel.querySelector('[data-loyalty-tier-progress-label]');
    const tierProgressPts = panel.querySelector('[data-loyalty-tier-progress-pts]');
    const tierProgressBar = panel.querySelector('[data-loyalty-tier-progress-bar]');
    const tierBenefits = panel.querySelector('[data-loyalty-tier-benefits]');

    if (state.nextTier) {
      if (tierProgressLabel) tierProgressLabel.textContent = t('loyalty.until_next_tier', 'Bis zu') + ' ' + state.nextTier.name.toUpperCase();
      if (tierProgressPts) tierProgressPts.textContent = state.nextTier.ptsToGo.toLocaleString('de-DE') + ' PTS';
      if (tierProgressBar) {
        const tierStart = state.tierIndex !== null ? window.eprisLoyalty.TIERS[state.tierIndex].threshold : 0;
        const tierEnd = state.nextTier.threshold;
        const range = Math.max(1, tierEnd - tierStart);
        const pct = Math.min(100, Math.max(0, ((state.pointsApproved - tierStart) / range) * 100));
        tierProgressBar.style.width = pct.toFixed(1) + '%';
      }
      if (tierBenefits) {
        const items = state.nextTier.benefits || [];
        tierBenefits.innerHTML = '<span class="epris-loyalty-panel__benefits-label">'
          + t('loyalty.unlock_label', 'Freischalten') + '</span>'
          + items.map(b => '<span class="epris-loyalty-panel__benefit-chip">' + escapeHtml(b) + '</span>').join('');
      }
    } else {
      if (tierProgressLabel) tierProgressLabel.textContent = t('loyalty.top_tier_reached', 'Höchster Tier erreicht');
      if (tierProgressPts) tierProgressPts.textContent = '✓';
      if (tierProgressBar) tierProgressBar.style.width = '100%';
      if (tierBenefits) {
        const privePerks = ['Permanent −15% Rabatt', 'Priority Shipping', 'Free Shipping', 'Early Access'];
        tierBenefits.innerHTML = '<span class="epris-loyalty-panel__benefits-label">'
          + t('loyalty.active', 'Deine Vorteile') + '</span>'
          + privePerks.map(b => '<span class="epris-loyalty-panel__benefit-chip">' + escapeHtml(b) + '</span>').join('');
      }
    }

    /* OVERVIEW: Next Reward */
    const nextRewardName = panel.querySelector('[data-loyalty-next-reward-name]');
    const nextRewardDist = panel.querySelector('[data-loyalty-next-reward-distance]');
    const nextRewardSection = panel.querySelector('[data-loyalty-next-reward-section]');
    if (state.nextReward && nextRewardSection) {
      nextRewardSection.hidden = false;
      if (nextRewardName) nextRewardName.textContent = state.nextReward.name;
      if (nextRewardDist) {
        nextRewardDist.textContent = t('loyalty.still', 'Noch') + ' '
          + state.nextReward.ptsToGo.toLocaleString('de-DE') + ' Pts';
      }
    } else if (nextRewardSection) {
      nextRewardSection.hidden = true;
    }

    /* REWARDS TAB */
    renderRewardsList(panel, state);

    /* HISTORY TAB — alle 4 echten Zahlen aus state (kein guesswork mehr) */
    setText(panel, '[data-loyalty-pts-approved]', (state.pointsRedeemable || state.pointsApproved || 0).toLocaleString('de-DE'));
    setText(panel, '[data-loyalty-pts-pending]', (state.pointsPending || 0).toLocaleString('de-DE'));
    setText(panel, '[data-loyalty-rewards-claimed]', (state.rewardsClaimedCount || 0).toLocaleString('de-DE'));
    setText(panel, '[data-loyalty-pts-spent]', (state.pointsSpent || 0).toLocaleString('de-DE'));
    setText(panel, '[data-loyalty-pts-lifetime]', (state.pointsLifetime || 0).toLocaleString('de-DE'));
  }

  function renderRewardsList(panel, state) {
    const list = panel.querySelector('[data-loyalty-rewards-list]');
    if (!list) return;
    const rewards = window.eprisLoyalty.REWARDS || [];
    const have = state.points || 0;
    const html = rewards.map(r => {
      const unlocked = have >= r.threshold;
      const pct = unlocked ? 100 : Math.min(100, (have / r.threshold) * 100);
      const togo = unlocked ? 0 : r.threshold - have;
      return [
        '<div class="epris-loyalty-reward-item ' + (unlocked ? 'is-unlocked' : 'is-locked') + '">',
          '<div class="epris-loyalty-reward-item__head">',
            '<span class="epris-loyalty-reward-item__name">' + escapeHtml(r.name) + '</span>',
            '<span class="epris-loyalty-reward-item__pts">' + r.threshold.toLocaleString('de-DE') + ' PTS</span>',
          '</div>',
          '<div class="epris-loyalty-reward-item__bar">',
            '<div class="epris-loyalty-reward-item__bar-fill" style="width:' + pct.toFixed(1) + '%"></div>',
          '</div>',
          '<div class="epris-loyalty-reward-item__status">',
            unlocked
              ? '<span class="epris-loyalty-reward-item__status-ok">✓ ' + t('loyalty.unlocked', 'Freigeschaltet') + '</span>'
              : '<span class="epris-loyalty-reward-item__status-togo">' + t('loyalty.still', 'Noch') + ' ' + togo.toLocaleString('de-DE') + ' Pts</span>',
          '</div>',
        '</div>'
      ].join('');
    }).join('');
    list.innerHTML = html;
  }

  /* ============== PDP SECTION (Ankündigungsleiste) ============== */
  function renderPdp(state) {
    document.querySelectorAll('[data-loyalty-pdp]').forEach(el => {
      if (!state.isLoggedIn) return;
      const tierEl = el.querySelector('[data-loyalty-tier-name]');
      const ptsEl = el.querySelector('[data-loyalty-pts-num]');
      if (tierEl) tierEl.textContent = (state.tierName || 'Silver').toUpperCase();
      if (ptsEl) ptsEl.textContent = (state.points || 0).toLocaleString('de-DE') + ' Pts';

      /* Smart-Hint: tier-up oder reward-unlock mit dieser Bestellung */
      const priceCents = parseInt(el.dataset.productPrice, 10) || 0;
      const earnableThis = window.eprisLoyalty.pointsForPrice(priceCents);
      const projected = state.points + earnableThis;
      const projectedApproved = state.pointsApproved + earnableThis;
      const hint = el.querySelector('[data-loyalty-pdp-hint]');
      const tierMini = el.querySelector('[data-loyalty-pdp-tier-mini]');
      const divider = el.querySelector('[data-loyalty-pdp-divider]');
      if (!hint) return;
      let hintHtml = '';
      if (state.nextTier && projectedApproved >= state.nextTier.threshold) {
        hintHtml = '🎉 ' + escapeHtml(state.nextTier.name.toUpperCase()) + ' ' + t('loyalty.unlocked', 'freigeschaltet');
      } else if (state.nextReward && projected >= state.nextReward.threshold) {
        hintHtml = '🎁 ' + escapeHtml(state.nextReward.name) + ' ' + t('loyalty.unlocked', 'freigeschaltet');
      }
      if (hintHtml) {
        hint.innerHTML = hintHtml;
        hint.hidden = false;
        if (tierMini) tierMini.hidden = true;
        if (divider) divider.hidden = true;
      } else {
        hint.hidden = true;
        if (tierMini) tierMini.hidden = false;
        if (divider) divider.hidden = false;
      }
    });
  }

  /* ============== CART DRAWER STRIP ============== */
  function renderCart(state) {
    document.querySelectorAll('[data-loyalty-cart]').forEach(el => {
      const cartTotal = parseInt(el.dataset.cartTotal, 10) || 0;
      const earnable = window.eprisLoyalty.pointsForCart(cartTotal);

      const ptsEl = el.querySelector('[data-loyalty-cart-pts]');
      if (ptsEl) ptsEl.textContent = earnable.toLocaleString('de-DE');

      /* Sub-Line: läuft IMMER (egal ob LoyaltyLion-state ready) — default = Euro-Wert.
         Smart-Hints überschreiben nur wenn state info verfügbar. */
      const sub = el.querySelector('[data-loyalty-cart-sub]');
      if (!sub) return;

      let html = '≈ ' + formatEuro(earnable / 100); /* 100 Pts = €1 default */
      let isHint = false;

      if (state.isLoggedIn) {
        const projected = state.points + earnable;
        const projectedApproved = state.pointsApproved + earnable;
        const nextTier = state.nextTier;
        const nextR = state.nextReward;
        if (nextTier && projectedApproved >= nextTier.threshold) {
          html = '🎉 ' + t('loyalty.with_order_reaches_tier', 'Mit dieser Bestellung erreichst du') + ' <strong>' + escapeHtml(nextTier.name.toUpperCase()) + '</strong>';
          isHint = true;
        } else if (nextR && projected >= nextR.threshold) {
          html = '🎁 <strong>' + escapeHtml(nextR.name) + '</strong> ' + t('loyalty.unlocked', 'freigeschaltet');
          isHint = true;
        } else if (nextR) {
          const stillToGo = Math.max(0, nextR.threshold - projected);
          html = t('loyalty.still', 'Noch') + ' <strong>' + stillToGo.toLocaleString('de-DE') + ' Pts</strong> '
            + t('loyalty.until', 'bis') + ' ' + escapeHtml(nextR.name);
          isHint = true;
        }
      }
      sub.innerHTML = html;
      sub.classList.toggle('is-hint', isHint);
    });
  }

  /* ============== PANEL OPEN/CLOSE ============== */
  function openPanel() {
    const panel = document.getElementById('epris-loyalty-panel');
    if (!panel) return;
    panel.removeAttribute('hidden');
    requestAnimationFrame(() => {
      panel.classList.add('is-visible');
      panel.setAttribute('aria-hidden', 'false');
    });
    document.querySelectorAll('[data-loyalty-badge]').forEach(b => b.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
  }
  function closePanel() {
    const panel = document.getElementById('epris-loyalty-panel');
    if (!panel) return;
    panel.classList.remove('is-visible');
    panel.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-loyalty-badge]').forEach(b => b.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!panel.classList.contains('is-visible')) panel.setAttribute('hidden', '');
    }, 320);
  }

  /* ============== INFO MODAL ============== */
  function openInfo() {
    const modal = document.getElementById('epris-loyalty-info-modal');
    if (!modal) return;
    modal.removeAttribute('hidden');
    requestAnimationFrame(() => {
      modal.classList.add('is-visible');
      modal.setAttribute('aria-hidden', 'false');
    });
    document.body.style.overflow = 'hidden';
  }
  function closeInfo() {
    const modal = document.getElementById('epris-loyalty-info-modal');
    if (!modal) return;
    modal.classList.remove('is-visible');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(() => {
      if (!modal.classList.contains('is-visible')) modal.setAttribute('hidden', '');
    }, 320);
  }

  /* ============== TABS ============== */
  function onTabClick(e) {
    const tab = e.target.closest('[data-loyalty-tab]');
    if (!tab) return;
    e.preventDefault();
    const which = tab.dataset.loyaltyTab;
    const panel = tab.closest('.epris-loyalty-panel') || document;
    panel.querySelectorAll('[data-loyalty-tab]').forEach(t => {
      const active = t.dataset.loyaltyTab === which;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    panel.querySelectorAll('[data-loyalty-pane]').forEach(p => {
      const active = p.dataset.loyaltyPane === which;
      p.classList.toggle('is-active', active);
      if (active) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    });
  }

  /* ============== REFERRAL SHARE ============== */
  function getReferralLink() {
    /* Versuche: LL.customer.referralLink → fallback auf account-Page */
    const ll = window.loyaltylion;
    if (ll && ll.customer && ll.customer.referralLink) return ll.customer.referralLink;
    if (ll && ll.customer && ll.customer.referralUrl) return ll.customer.referralUrl;
    /* Fallback: pages-link mit ?ref={customer.id} */
    const cid = (ll && ll.customer && ll.customer.id) || '';
    const base = location.origin + '/pages/epris-loyalty-programm';
    return cid ? base + '?ref=' + encodeURIComponent(cid) : base;
  }
  function getReferralMessage() {
    const link = getReferralLink();
    return t('loyalty.refer_share_text', 'Ich liebe ÉPRIS — und du bekommst €8 Rabatt auf deine erste Bestellung. Hier mein Link: ') + link;
  }
  function showShareHint(text) {
    document.querySelectorAll('[data-loyalty-share-hint]').forEach(el => {
      el.textContent = text;
      el.hidden = false;
      clearTimeout(el._hintTimer);
      el._hintTimer = setTimeout(() => { el.hidden = true; }, 2800);
    });
  }
  function handleShare(action) {
    const link = getReferralLink();
    const msg = getReferralMessage();
    if (action === 'copy') {
      const tryCopy = navigator.clipboard && navigator.clipboard.writeText
        ? navigator.clipboard.writeText(link)
        : Promise.reject();
      tryCopy.then(
        () => showShareHint('✓ ' + t('loyalty.refer_copied', 'Link kopiert')),
        () => {
          /* Fallback: temp-textarea */
          const ta = document.createElement('textarea');
          ta.value = link; ta.style.position = 'fixed'; ta.style.opacity = '0';
          document.body.appendChild(ta); ta.select();
          try { document.execCommand('copy'); showShareHint('✓ ' + t('loyalty.refer_copied', 'Link kopiert')); }
          catch (e) { showShareHint(t('loyalty.refer_copy_failed', 'Kopieren fehlgeschlagen')); }
          document.body.removeChild(ta);
        }
      );
    } else if (action === 'whatsapp') {
      window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank', 'noopener');
    } else if (action === 'email') {
      const subject = t('loyalty.refer_email_subject', '€8 Rabatt für dich bei ÉPRIS');
      window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(msg);
    }
  }

  /* ============== Click delegation ============== */
  function onClick(e) {
    /* Header-Badge → toggle Panel */
    const badge = e.target.closest('[data-loyalty-badge]');
    if (badge) {
      e.preventDefault();
      const panel = document.getElementById('epris-loyalty-panel');
      if (panel && panel.classList.contains('is-visible')) closePanel();
      else openPanel();
      return;
    }
    if (e.target.closest('[data-loyalty-panel-close]')) {
      e.preventDefault(); closePanel(); return;
    }
    if (e.target.closest('[data-loyalty-open-panel]')) {
      e.preventDefault(); openPanel(); return;
    }
    /* Info-Modal */
    if (e.target.closest('[data-loyalty-open-info]')) {
      e.preventDefault(); openInfo(); return;
    }
    if (e.target.closest('[data-loyalty-info-close]')) {
      e.preventDefault(); closeInfo(); return;
    }
    /* Share */
    const shareBtn = e.target.closest('[data-loyalty-share]');
    if (shareBtn) {
      e.preventDefault();
      handleShare(shareBtn.dataset.loyaltyShare);
      return;
    }
  }

  /* Helpers */
  function setText(scope, sel, txt) {
    scope.querySelectorAll(sel).forEach(el => { el.textContent = txt; });
  }
  /* Smooth Count-Up: animiert Pts-Num von letztem Wert zu Ziel-Wert.
     Benutzt requestAnimationFrame, ease-out. Re-runs bei state-update OK. */
  function animateCountUp(el, targetNum) {
    const start = parseInt((el.textContent || '0').replace(/\D/g, ''), 10) || 0;
    const target = Math.round(targetNum);
    if (start === target) { el.textContent = target.toLocaleString('de-DE'); return; }
    if (Math.abs(target - start) < 3) { el.textContent = target.toLocaleString('de-DE'); return; }
    const duration = 800;
    const t0 = performance.now();
    cancelAnimationFrame(el._countupRaf || 0);
    const tick = (now) => {
      const elapsed = now - t0;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3); /* ease-out cubic */
      const v = Math.round(start + (target - start) * eased);
      el.textContent = v.toLocaleString('de-DE');
      if (p < 1) el._countupRaf = requestAnimationFrame(tick);
    };
    el._countupRaf = requestAnimationFrame(tick);
  }
  function formatEuro(eur) {
    return '€' + (eur || 0).toFixed(2).replace('.', ',');
  }
  function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready);
  } else {
    ready();
  }
})();
